import React from 'react';

import { NetworkTier } from 'Containers/ResultPage/CompareModal';
import { CombinedNetworkEssentials } from 'Containers/ViewPlanPage/PlanEssentialsCard/CombinedNetworkEssentials';
import { ViewPlanPageCard } from 'Containers/ViewPlanPage/ViewPlanPageCard';
import { BreakdownTag } from 'Containers/ViewPlanPage/ViewPlanPageCard/BreakdownTag';
import { useTextContext } from 'Contexts/textContext';
import { FormattedTooltipProps, Numbers, Paragraph } from 'DesignLibrary/atoms';
import { TooltipPlanDetails } from 'DesignLibrary/molecules';
import { IconBlockProps } from 'DesignLibrary/molecules/IconBlock';
import Plan from 'Models/plan';
import Text from 'Shared/Text';
import { Recommendation } from 'Types/entities';
import { toDollars } from 'Utils/helpers';

import { SplitCostRow, SplitCostWrapper } from './styled';
import { PlanCardRow } from '../_shared/styled';

export interface PlanEssentialsCardProps {
  recommendation: Recommendation;
  selectedTier: NetworkTier;
  isNetworkTierToggleEnabled: boolean;
}

export const PlanEssentialsCard: React.FC<PlanEssentialsCardProps> = ({
  recommendation,
  selectedTier,
  isNetworkTierToggleEnabled,
}) => {
  const { retrieveContentfulData } = useTextContext();

  const noOonCoverageText =
    retrieveContentfulData<string>('health_section.compare_plan_text.compareModal.noOonCoverage') ||
    'No out-of-network coverage';
  const NO_OON_COVERAGE = <Paragraph size="small">{noOonCoverageText}</Paragraph>;

  const title = retrieveContentfulData<string>(
    'health_details_section.plan_details.plan_essentials_card_title',
  );
  const icon: IconBlockProps = {
    icon: 'Sparkle',
    color: 'blue',
  };

  // const planTypeTooltip: FormattedTooltipProps = {
  //   text: 'TODO FIX ME - text',
  //   title: 'TODO FIX ME - title',
  //   underline: planTypeUnderline,
  // };

  const inNetworkDeductibleTooltip = {
    ...retrieveContentfulData<FormattedTooltipProps>('tool_tips.in_network_deductible'),
  };

  const outOfNetworkDeductibleTooltip = {
    ...retrieveContentfulData<FormattedTooltipProps>('tool_tips.out_of_network_deductible'),
  };

  const inNetworkOopLimitTooltip = {
    ...retrieveContentfulData<FormattedTooltipProps>('tool_tips.in_network_out_of_pocket_limit'),
  };

  const outOfNetworkOopLimitTooltip = {
    ...retrieveContentfulData<FormattedTooltipProps>('tool_tips.out_of_network_out_of_pocket_limit'),
  };

  const plan = new Plan(recommendation);

  const {
    inNetworkDeductible,
    inNetworkDrugDeductible,
    inNetworkMedDeductible,
    oonDeductible,
    oonDrugDeductible,
    oonMedDeductible,
    inNetworkOopMax,
    inNetworkMedOopMax,
    inNetworkDrugOopMax,
    oonOopMax,
    oonDrugOopMax,
    oonMedOopMax,
  } = plan;

  const formatDeductibleOrOop = (value) => {
    if (!value) {
      if (value === 0) {
        return toDollars(String(value));
      }
      return retrieveContentfulData<string>('health_section.null_plan_detail_text.deductibleAndOopMax');
    }
    return toDollars(String(value), Number.isInteger(value) ? 0 : 2);
  };

  // Define In/Out Network Deductibles
  const inNetworkCompDed = formatDeductibleOrOop(inNetworkDeductible);
  const inNetworkMedDed = formatDeductibleOrOop(inNetworkMedDeductible);
  const inNetworkDrugDed = formatDeductibleOrOop(inNetworkDrugDeductible);

  const outNetworkCompDed = formatDeductibleOrOop(oonDeductible);
  const outNetworkMedDed = formatDeductibleOrOop(oonMedDeductible);
  const outNetworkDrugDed = formatDeductibleOrOop(oonDrugDeductible);

  // Define In/Out Network OOP Limits
  const inNetworkOopLimitComp = formatDeductibleOrOop(inNetworkOopMax);
  const inNetworkOopLimitMed = formatDeductibleOrOop(inNetworkMedOopMax);
  const inNetworkOopLimitDrug = formatDeductibleOrOop(inNetworkDrugOopMax);

  const outNetworkOopLimitComp = formatDeductibleOrOop(oonOopMax);
  const outNetworkOopLimitMed = formatDeductibleOrOop(oonMedOopMax);
  const outNetworkOopLimitDrug = formatDeductibleOrOop(oonDrugOopMax);

  const showSplitInNetworkMedicalAndDrugDed = !plan.getIsInNetworkDeductibleComprehensive();
  const showSplitOutNetworkMedicalAndDrugDed = !plan.getIsOonDeductibleComprehensive();
  const showSplitInNetworkMedicalAndDrugOopLimit = !plan.getIsInNetworkOopLimitComprehensive();
  const showSplitOutNetworkMedicalAndDrugOopLimit = !plan.getIsOonOopLimitComprehensive();

  const renderDeductibles = () => {
    return (
      <PlanCardRow className="planCardRow">
        <TooltipPlanDetails
          tooltip={inNetworkDeductibleTooltip}
          keyHint={`plan-${plan.planId}-in-network-deductible`}
        >
          {inNetworkDeductibleTooltip.underline as string}
        </TooltipPlanDetails>
        {showSplitInNetworkMedicalAndDrugDed ? (
          <SplitCostWrapper>
            <SplitCostRow>
              <BreakdownTag>
                <Text field="health_section.comprehensive_plan_terms.medical" />
              </BreakdownTag>
              <Numbers className="in-network-med-ded">{inNetworkMedDed}</Numbers>
            </SplitCostRow>

            <SplitCostRow>
              <BreakdownTag>
                <Text field="health_section.comprehensive_plan_terms.drug" />
              </BreakdownTag>
              <Numbers className="in-network-drug-ded">{inNetworkDrugDed}</Numbers>
            </SplitCostRow>
          </SplitCostWrapper>
        ) : (
          <Numbers className="in-network-comprehensive-ded">{inNetworkCompDed}</Numbers>
        )}
      </PlanCardRow>
    );
  };

  if (plan.hasCombinedNetworkDeductible() && plan.hasCombinedNetworkOopMax()) {
    return <CombinedNetworkEssentials plan={plan} />;
  }

  if (!isNetworkTierToggleEnabled) {
    const hasOon = !!plan.individualThresholds?.out_of_network;
    return (
      <ViewPlanPageCard title={title} icon={icon}>
        {renderDeductibles()}
        {hasOon && (
          <PlanCardRow className="planCardRow">
            <TooltipPlanDetails
              tooltip={outOfNetworkDeductibleTooltip}
              keyHint={`plan-${plan.planId}-oon-deductible`}
            >
              {outOfNetworkDeductibleTooltip.underline as string}
            </TooltipPlanDetails>
            {showSplitOutNetworkMedicalAndDrugDed ? (
              <SplitCostWrapper>
                <SplitCostRow>
                  <BreakdownTag>
                    <Text field="health_section.comprehensive_plan_terms.medical" />
                  </BreakdownTag>
                  <Numbers className="out-network-med-ded">{outNetworkMedDed}</Numbers>
                </SplitCostRow>
                <SplitCostRow>
                  <BreakdownTag>
                    <Text field="health_section.comprehensive_plan_terms.drug" />
                  </BreakdownTag>
                  <Numbers className="out-network-drug-ded">{outNetworkDrugDed}</Numbers>
                </SplitCostRow>
              </SplitCostWrapper>
            ) : (
              <Numbers className="out-network-comprehensive-ded">{outNetworkCompDed}</Numbers>
            )}
          </PlanCardRow>
        )}
        <PlanCardRow className="planCardRow">
          <TooltipPlanDetails
            tooltip={inNetworkOopLimitTooltip}
            keyHint={`plan-${plan.planId}-in-network-oop-limit`}
          >
            {inNetworkOopLimitTooltip.underline as string}
          </TooltipPlanDetails>
          {showSplitInNetworkMedicalAndDrugOopLimit ? (
            <SplitCostWrapper>
              <SplitCostRow>
                <BreakdownTag>
                  <Text field="health_section.comprehensive_plan_terms.medical" />
                </BreakdownTag>
                <Numbers className="in-network-med-oop">{inNetworkOopLimitMed}</Numbers>
              </SplitCostRow>
              <SplitCostRow>
                <BreakdownTag>
                  <Text field="health_section.comprehensive_plan_terms.drug" />
                </BreakdownTag>
                <Numbers className="in-network-drug-oop">{inNetworkOopLimitDrug}</Numbers>
              </SplitCostRow>
            </SplitCostWrapper>
          ) : (
            <Numbers className="in-network-comprehensive-oop">{inNetworkOopLimitComp}</Numbers>
          )}
        </PlanCardRow>
        {hasOon && (
          <PlanCardRow className="planCardRow">
            <TooltipPlanDetails
              tooltip={outOfNetworkOopLimitTooltip}
              keyHint={`plan-${plan.planId}-oon-oop-limit`}
            >
              {outOfNetworkOopLimitTooltip.underline as string}
            </TooltipPlanDetails>
            {showSplitOutNetworkMedicalAndDrugOopLimit ? (
              <SplitCostWrapper>
                <SplitCostRow>
                  <BreakdownTag>
                    <Text field="health_section.comprehensive_plan_terms.medical" />
                  </BreakdownTag>
                  <Numbers className="out-network-med-oop">{outNetworkOopLimitMed}</Numbers>
                </SplitCostRow>
                <SplitCostRow>
                  <BreakdownTag>
                    <Text field="health_section.comprehensive_plan_terms.drug" />
                  </BreakdownTag>
                  <Numbers className="out-network-drug-oop">{outNetworkOopLimitDrug}</Numbers>
                </SplitCostRow>
              </SplitCostWrapper>
            ) : (
              <Numbers className="out-network-comprehensive-oop">{outNetworkOopLimitComp}</Numbers>
            )}
          </PlanCardRow>
        )}
      </ViewPlanPageCard>
    );
  }

  const thresholds = plan.getPlanDetailsForNetworkTier(selectedTier);
  const dedTooltip =
    selectedTier === 'out_of_network' ? outOfNetworkDeductibleTooltip : inNetworkDeductibleTooltip;
  const oopTooltip =
    selectedTier === 'out_of_network' ? outOfNetworkOopLimitTooltip : inNetworkOopLimitTooltip;
  const dedLabel = isNetworkTierToggleEnabled
    ? retrieveContentfulData<string>('health_section.compare_plan_text.compareModal.deductible') ||
      'Deductible'
    : (dedTooltip.underline as string);
  const oopLabel = isNetworkTierToggleEnabled
    ? retrieveContentfulData<string>('health_section.compare_plan_text.compareModal.oopLimit') ||
      'Out-of-Pocket Limit'
    : (oopTooltip.underline as string);

  const renderTierDeductible = () => {
    if (selectedTier === 'out_of_network' && !plan.hasOonCoverage()) return NO_OON_COVERAGE;
    if (!thresholds) return <Numbers>{formatDeductibleOrOop(null)}</Numbers>;
    const { medical_and_drug_deductible, medical_deductible, drug_deductible } = thresholds;
    if (medical_and_drug_deductible !== null) {
      return <Numbers>{formatDeductibleOrOop(medical_and_drug_deductible)}</Numbers>;
    }
    if (medical_deductible !== null || drug_deductible !== null) {
      return (
        <SplitCostWrapper>
          <SplitCostRow>
            <BreakdownTag>
              <Text field="health_section.comprehensive_plan_terms.medical" />
            </BreakdownTag>
            <Numbers>{formatDeductibleOrOop(medical_deductible)}</Numbers>
          </SplitCostRow>
          <SplitCostRow>
            <BreakdownTag>
              <Text field="health_section.comprehensive_plan_terms.drug" />
            </BreakdownTag>
            <Numbers>{formatDeductibleOrOop(drug_deductible)}</Numbers>
          </SplitCostRow>
        </SplitCostWrapper>
      );
    }
    return <Numbers>{formatDeductibleOrOop(null)}</Numbers>;
  };

  const renderTierOopMax = () => {
    if (selectedTier === 'out_of_network' && !plan.hasOonCoverage()) return NO_OON_COVERAGE;
    if (!thresholds) return <Numbers>{formatDeductibleOrOop(null)}</Numbers>;
    const { medical_and_drug_oop_max, medical_oop_max, drug_oop_max } = thresholds;
    if (medical_and_drug_oop_max !== null) {
      return <Numbers>{formatDeductibleOrOop(medical_and_drug_oop_max)}</Numbers>;
    }
    if (medical_oop_max !== null || drug_oop_max !== null) {
      return (
        <SplitCostWrapper>
          <SplitCostRow>
            <BreakdownTag>
              <Text field="health_section.comprehensive_plan_terms.medical" />
            </BreakdownTag>
            <Numbers>{formatDeductibleOrOop(medical_oop_max)}</Numbers>
          </SplitCostRow>
          <SplitCostRow>
            <BreakdownTag>
              <Text field="health_section.comprehensive_plan_terms.drug" />
            </BreakdownTag>
            <Numbers>{formatDeductibleOrOop(drug_oop_max)}</Numbers>
          </SplitCostRow>
        </SplitCostWrapper>
      );
    }
    return <Numbers>{formatDeductibleOrOop(null)}</Numbers>;
  };

  return (
    <ViewPlanPageCard title={title} icon={icon}>
      <PlanCardRow className="planCardRow">
        <TooltipPlanDetails tooltip={dedTooltip} keyHint={`plan-${plan.planId}-deductible`}>
          {dedLabel}
        </TooltipPlanDetails>
        {renderTierDeductible()}
      </PlanCardRow>

      <PlanCardRow className="planCardRow">
        <TooltipPlanDetails tooltip={oopTooltip} keyHint={`plan-${plan.planId}-oop-limit`}>
          {oopLabel}
        </TooltipPlanDetails>
        {renderTierOopMax()}
      </PlanCardRow>
    </ViewPlanPageCard>
  );
};
